/**
 * Customer-side onboarding tab components.
 * Contains: InvitationsTab, WorkflowsTab, DatasetsTab, PendingReviewsTab
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  UserPlus,
  FileText,
  CheckCircle2,
  MoreHorizontal,
  Send,
  Eye,
  Plus,
  ClipboardList,
  Building2,
  RefreshCw,
  Link,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { SupplierInvitation, OnboardingWorkflow, FilledDataset, OnboardingDataset, onboardingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { statusColors } from './onboardingConstants';
import { formatDistanceToNow, format } from 'date-fns';

// --- Invitations Tab ---
interface InvitationsTabProps {
  filteredInvitations: SupplierInvitation[];
  onSendInvitation: (id: number) => void;
  onResendInvitation: (id: number) => void;
}

export function InvitationsTab({
  filteredInvitations,
  onSendInvitation,
  onResendInvitation,
}: InvitationsTabProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Supplier Invitations</CardTitle>
          <CardDescription>Track and manage supplier onboarding invitations</CardDescription>
        </div>
        <Button onClick={() => navigate('/onboarding/invitations/new')}>
          <Plus className="h-4 w-4 mr-2" />New Invitation
        </Button>
      </CardHeader>
      <CardContent>
        {filteredInvitations.length === 0 ? (
          <div className="text-center py-8">
            <UserPlus className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No invitations yet</h3>
            <p className="text-muted-foreground">Use the "New Invitation" button above to invite a supplier.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{invitation.invitee_company_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{invitation.invitee_contact_name}</div>
                      <div className="text-sm text-muted-foreground">{invitation.invitee_email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[invitation.status] || statusColors['pending']}>
                      {invitation.status?.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {invitation.sent_at ? (
                      <span className="text-sm">
                        {formatDistanceToNow(new Date(invitation.sent_at), { addSuffix: true })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Not sent</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {invitation.expires_at ? (
                      <span className="text-sm">{format(new Date(invitation.expires_at), 'MMM d, yyyy')}</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {invitation.supplier && (
                          <DropdownMenuItem onClick={() => navigate(`/suppliers/wizard/${invitation.supplier}?step=1`)}>
                            <Eye className="h-4 w-4 mr-2" />View Supplier Details
                          </DropdownMenuItem>
                        )}
                        {invitation.status === 'pending' && (
                          <DropdownMenuItem onClick={() => onSendInvitation(invitation.id)}>
                            <Send className="h-4 w-4 mr-2" />Send Invitation
                          </DropdownMenuItem>
                        )}
                        {(invitation.status === 'sent' || invitation.status === 'opened' || invitation.status === 'expired') && (
                          <DropdownMenuItem onClick={() => onResendInvitation(invitation.id)}>
                            <RefreshCw className="h-4 w-4 mr-2" />Resend Invitation
                          </DropdownMenuItem>
                        )}
                        {invitation.invitation_token && (
                          <DropdownMenuItem onClick={() => {
                            const link = `${window.location.origin}/onboarding/accept/${invitation.invitation_token}`;
                            navigator.clipboard.writeText(link);
                            import('sonner').then(({ toast }) => toast.success('Invite link copied to clipboard'));
                          }}>
                            <Link className="h-4 w-4 mr-2" />Copy Invite Link
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// --- Workflows Tab ---
interface WorkflowsTabProps {
  filteredWorkflows: OnboardingWorkflow[];
}

export function WorkflowsTab({ filteredWorkflows }: WorkflowsTabProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Onboarding Workflows</CardTitle>
        <CardDescription>Track supplier progress through onboarding steps</CardDescription>
      </CardHeader>
      <CardContent>
        {filteredWorkflows.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardList className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No active onboardings</h3>
            <p className="text-muted-foreground">Onboardings will appear here when suppliers accept invitations</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Supplier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Risk Score</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWorkflows.map((workflow) => (
                <TableRow key={workflow.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{workflow.supplier_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[workflow.status] || statusColors['pending']}>
                      {workflow.status?.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={workflow.progress_percentage || 0} className="w-24" />
                      <span className="text-sm text-muted-foreground">{Math.round(workflow.progress_percentage || 0)}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {workflow.calculated_risk_score !== null && workflow.calculated_risk_score !== undefined ? (
                      <Badge variant={
                        workflow.calculated_risk_score > 70 ? 'destructive' :
                        workflow.calculated_risk_score > 40 ? 'secondary' : 'default'
                      }>
                        {workflow.calculated_risk_score}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {workflow.started_at ? (
                      <span className="text-sm">{formatDistanceToNow(new Date(workflow.started_at), { addSuffix: true })}</span>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {workflow.supplier && (
                          <DropdownMenuItem onClick={() => navigate(`/suppliers/wizard/${workflow.supplier}?step=1`)}>
                            <Eye className="h-4 w-4 mr-2" />View Supplier Details
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// --- Datasets Tab ---
interface DatasetsTabProps {
  datasets: OnboardingDataset[];
}

export function DatasetsTab({ datasets }: DatasetsTabProps) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Questionnaire Templates</CardTitle>
          <CardDescription>Manage onboarding questionnaires and compliance forms</CardDescription>
        </div>
        <Button onClick={() => navigate('/onboarding/datasets/new')}>
          <Plus className="h-4 w-4 mr-2" />Create Template
        </Button>
      </CardHeader>
      <CardContent>
        {datasets.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No Questionnaire Templates</h3>
            <p className="text-muted-foreground mb-4">Create questionnaire templates for supplier onboarding</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Questions</TableHead>
                <TableHead>Pass Score</TableHead>
                <TableHead>Mandatory</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {datasets.map((dataset) => (
                <TableRow key={dataset.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{dataset.name}</div>
                      {dataset.description && (
                        <div className="text-sm text-muted-foreground truncate max-w-[300px]">{dataset.description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{dataset.dataset_type.replace(/_/g, ' ')}</Badge>
                  </TableCell>
                  <TableCell>{dataset.questions_count ?? '-'}</TableCell>
                  <TableCell>{dataset.pass_score}%</TableCell>
                  <TableCell>
                    {dataset.is_mandatory ? (
                      <Badge className="bg-blue-100 text-blue-800">Required</Badge>
                    ) : (
                      <span className="text-muted-foreground">Optional</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {dataset.is_active ? (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => navigate(`/onboarding/datasets/${dataset.id}`)}>
                      <Eye className="h-4 w-4 mr-1" />View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// --- Pending Reviews Tab ---
interface FilledDatasetWithDetails extends FilledDataset {
  supplier_name?: string;
  dataset_name?: string;
}

interface PendingReviewsTabProps {
  pendingReviews: FilledDatasetWithDetails[];
  onAction?: () => void;
}

export function PendingReviewsTab({ pendingReviews, onAction }: PendingReviewsTabProps) {
  const navigate = useNavigate();
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null);
  const [approvingWorkflow, setApprovingWorkflow] = useState<number | null>(null);

  // Group reviews by supplier
  const grouped = pendingReviews.reduce<Record<string, { name: string; supplierId?: number; reviews: FilledDatasetWithDetails[] }>>((acc, review) => {
    const key = review.supplier_name || `supplier-${review.supplier}`;
    if (!acc[key]) {
      acc[key] = { name: review.supplier_name || 'Unknown Supplier', supplierId: review.supplier, reviews: [] };
    }
    acc[key].reviews.push(review);
    return acc;
  }, {});

  const suppliers = Object.values(grouped);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Due Diligence</CardTitle>
        <CardDescription>
          {/* CR-4799: now covers submitted, under-review, more-info-requested and
              rejected questionnaires so customers can track every outstanding
              action from one place instead of losing rejected items. */}
          Questionnaires the supplier has sent you and any you have rejected or sent back for more information. Review each one, then approve the supplier.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {suppliers.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-medium">All caught up!</h3>
            <p className="text-muted-foreground">No questionnaires pending review at this time</p>
          </div>
        ) : (
          <div className="space-y-4">
            {suppliers.map((supplier) => {
              const isExpanded = expandedSupplier === supplier.name;
              const scoredReviews = supplier.reviews.filter(r => r.score_percentage != null && !isNaN(Number(r.score_percentage)));
              const totalScore = scoredReviews.reduce((sum, r) => sum + Number(r.score_percentage || 0), 0);
              const avgScore = scoredReviews.length > 0 ? Math.round(totalScore / scoredReviews.length) : null;
              // Badge reflects the aggregate questionnaire state the customer
              // actually needs to act on:
              //  - "Awaiting Review" while any questionnaire is still submitted/under_review
              //  - "Approved" once the customer has signed off on each
              //  - "Needs Attention" when anything explicitly failed
              const allApprovedBadge = supplier.reviews.every(r => r.status === 'approved');
              const anyExplicitlyFailed = supplier.reviews.some(r => r.passed === false || r.status === 'rejected');
              const badgeLabel = anyExplicitlyFailed
                ? 'Needs Attention'
                : allApprovedBadge ? 'Approved' : 'Awaiting Review';
              const badgeClass = anyExplicitlyFailed
                ? 'bg-red-100 text-red-800'
                : allApprovedBadge ? 'bg-green-600 text-white' : 'bg-blue-100 text-blue-800';
              const latestSubmit = supplier.reviews.reduce((latest, r) => {
                if (!r.submitted_at) return latest;
                return !latest || new Date(r.submitted_at) > new Date(latest) ? r.submitted_at : latest;
              }, '' as string);

              return (
                <div key={supplier.name} className="border rounded-lg overflow-hidden">
                  {/* Supplier header - click to expand */}
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedSupplier(isExpanded ? null : supplier.name)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <Building2 className="h-5 w-5 text-blue-700" />
                      </div>
                      <div>
                        <p className="font-semibold">{supplier.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {supplier.reviews.length} questionnaire{supplier.reviews.length !== 1 ? 's' : ''} submitted
                          {latestSubmit && ` · ${formatDistanceToNow(new Date(latestSubmit), { addSuffix: true })}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {avgScore !== null && (
                        <Badge className={avgScore >= 70 ? 'bg-green-100 text-green-800' : avgScore >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                          Avg Score: {avgScore}%
                        </Badge>
                      )}
                      <Badge variant="outline" className={badgeClass}>
                        {badgeLabel}
                      </Badge>
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </div>
                  </div>

                  {/* Expanded: questionnaire details + actions */}
                  {isExpanded && (
                    <div className="border-t bg-muted/10 p-4 space-y-3">
                      {supplier.reviews.map((review) => {
                        // CR-4799: surface the specific state the customer is
                        // looking at. Rejected/more_info_required rows were
                        // previously invisible; now they sit here with a
                        // colour-coded badge and an actionable label.
                        const statusLabelMap: Record<string, { label: string; className: string }> = {
                          submitted: { label: 'Awaiting Review', className: 'bg-blue-100 text-blue-800' },
                          under_review: { label: 'Under Review', className: 'bg-purple-100 text-purple-800' },
                          more_info_required: { label: 'More Info Requested', className: 'bg-amber-100 text-amber-800' },
                          rejected: { label: 'Rejected — Awaiting Resubmit', className: 'bg-red-100 text-red-800' },
                          approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
                        };
                        const statusBadge = review.status ? statusLabelMap[review.status] : undefined;
                        return (
                        <div key={review.id} className="flex items-center gap-4 p-3 bg-white rounded-md border">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium">{review.dataset_name || 'Questionnaire'}</p>
                              {statusBadge && (
                                <Badge variant="outline" className={statusBadge.className}>
                                  {statusBadge.label}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Score: {Math.round(review.score_percentage || 0)}%
                              {review.passed !== undefined && (review.passed ? ' · Passed' : ' · Failed')}
                              {review.expires_at && ` · Expires: ${format(new Date(review.expires_at), 'MMM d, yyyy')}`}
                              {review.renewal_status === 'due_soon' && ' · ⚠️ Renewal due soon'}
                              {review.renewal_status === 'overdue' && ' · 🔴 Overdue'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Progress value={review.completion_percentage} className="w-20" />
                            <Button size="sm" variant="outline" onClick={() => navigate(`/onboarding/review/${review.id}`)}>
                              <Eye className="h-3.5 w-3.5 mr-1" />Review
                            </Button>
                          </div>
                        </div>
                        );
                      })}

                      {/* Approve/Reject supplier actions */}
                      {(() => {
                        const workflowId = supplier.reviews.find(r => r.onboarding)?.onboarding;
                        const allApproved = supplier.reviews.every(r => r.status === 'approved');
                        const canApproveSupplier = !!workflowId && allApproved;
                        return (
                          <div className="flex items-center justify-between pt-3 border-t gap-3 flex-wrap">
                            <p className="text-xs text-muted-foreground">
                              {allApproved
                                ? 'All questionnaires approved. Finalise the supplier below.'
                                : 'Review and approve each questionnaire above — then finalise the supplier.'}
                            </p>
                            <div className="flex gap-2">
                              {supplier.supplierId && (
                                <Button variant="outline" size="sm" onClick={() => navigate(`/suppliers/wizard/${supplier.supplierId}?step=1`)}>
                                  View Full Profile
                                </Button>
                              )}
                              <Button
                                size="sm"
                                disabled={!canApproveSupplier || approvingWorkflow === workflowId}
                                title={canApproveSupplier ? undefined : 'Approve all questionnaires first'}
                                onClick={async () => {
                                  if (!workflowId) return;
                                  setApprovingWorkflow(workflowId);
                                  try {
                                    await onboardingAPI.approveWorkflow(workflowId);
                                    toast.success(`${supplier.name} approved.`);
                                    onAction?.();
                                  } catch (err: unknown) {
                                    console.error('Failed to approve supplier:', err);
                                    const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to approve supplier';
                                    toast.error(msg);
                                  } finally {
                                    setApprovingWorkflow(null);
                                  }
                                }}
                              >
                                {approvingWorkflow === workflowId ? 'Approving…' : 'Approve Supplier'}
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
